import { IBusinessAdminView } from './businessAdminService';
import { Api } from './axios-config';
import { IProductView } from './productService';
import { IBusinessEmployeeView } from './employeeService';
import { IServiceView } from './serviceService';
import { SubscriptionView } from './subscriptionService';

export interface IBusinessPublicView {
  id: number;
  name: string;
  slug?: string;
  address: string;
  profilePicture?: string;
  phone?: string;
  instagram?: string;
  facebook?: string;
  website?: string;
  paymentMethods?: string;
  weeklySchedules?: IDayScheduleView[];
}

export interface IBusinessView {
  id: number;
  name: string;
  slug?: string;
  address: string;
  cnpj?: string;
  profilePicture?: string;
  hideFromSearch: boolean;
  cardFeeDebit: number;
  cardFeeCredit: number;
  appointmentLeadTimeMinutes: number;
  isBlocked?: boolean;
  onboardingStep: number;
  productCommissionUsesGrossRevenue?: boolean;
  requireOpenCaixa?: boolean;
  autoCloseCaixaEnabled?: boolean;
  autoCloseCaixaTime?: string;
  phone?: string;
  instagram?: string;
  facebook?: string;
  website?: string;
  paymentMethods?: string;
  predefinedMessages?: string;
  subscription?: SubscriptionView;
  employees?: IBusinessEmployeeView[];
  admins: IBusinessAdminView[];
  services?: IServiceView[];
  products?: IProductView[];
  weeklySchedules?: IDayScheduleView[];
}

export interface IDayScheduleView {
  id: number;
  businessId?: number;
  businessEmployeeId?: number;
  dayOfWeek: number;
  dayName: string;
  isClosed: boolean;
  startTime?: string | null;
  endTime?: string | null;
}

const getById = async (id: number): Promise<IBusinessView> => {
  try {
    const { data } = await Api.get<IBusinessView>(
      `/business/${id}`
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const getPublic = async (): Promise<IBusinessPublicView[]> => {
  try {
    const { data } = await Api.get<IBusinessPublicView[]>(
      '/business/public'
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const get = async (): Promise<IBusinessView[]> => {
  try {
    const { data } = await Api.get<IBusinessView[]>(
      '/business'
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export interface ICreateBusiness {
  name: string;
  address: string;
  cnpj?: string;
  phone?: string;
  instagram?: string;
  facebook?: string;
  website?: string;
  paymentMethods?: string;
  planSlug?: string;
  weeklySchedules: { dayOfWeek: number; isClosed: boolean; startTime?: string | null; endTime?: string | null }[];
}

const create = async (body: ICreateBusiness): Promise<number> => {
  try {
    const { data } = await Api.post(
      '/business',
      body
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export interface IUpdateBusiness {
  id: number;
  name: string;
  slug?: string;
  address: string;
  cnpj?: string;
  profilePicture?: string;
  hideFromSearch: boolean;
  cardFeeDebit: number;
  cardFeeCredit: number;
  appointmentLeadTimeMinutes: number;
  productCommissionUsesGrossRevenue?: boolean;
  requireOpenCaixa?: boolean;
  autoCloseCaixaEnabled?: boolean;
  autoCloseCaixaTime?: string;
  phone?: string;
  instagram?: string;
  facebook?: string;
  website?: string;
  paymentMethods?: string;
  predefinedMessages?: string;
  weeklySchedules?: { dayOfWeek: number; isClosed: boolean; startTime?: string | null; endTime?: string | null }[];
}

const update = async (body: IUpdateBusiness): Promise<IBusinessView> => {
  try {
    const { data } = await Api.put(
      '/business',
      body
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const remove = async (id: number): Promise<void> => {
  try {
    await Api.delete(
      `/business/${id}`
    );

  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const uploadFile = async (id: number, file: FormData): Promise<string> => {
  try {
    const { data } = await Api.post<string>(
      `/business/${id}/upload`, file
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const deleteFile = async (id: number): Promise<string> => {
  try {
    const { data } = await Api.delete<string>(
      `/business/${id}/profile-picture`
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const updateOnboardingStep = async (businessId: number, step: number): Promise<void> => {
  await Api.patch(`/business/${businessId}/onboarding-step`, { step });
};

export const businessService = {
  getById,
  getPublic,
  get,
  create,
  update,
  remove,
  uploadFile,
  deleteFile,
  updateOnboardingStep,
};